import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAlert } from '../context/AlertContext';
import './StatsNew.css';

function StatsNew() {
  const [stats, setStats] = useState({
    followers: [],
    subscribers: [],
    donations: [],
    views: []
  });
  const [timeRange, setTimeRange] = useState('7d'); // 24h, 7d, 30d, 90d, all
  const [platform, setPlatform] = useState('all'); // all, twitch-main, youtube, etc.
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    totalFollowers: 0,
    totalSubs: 0,
    totalDonations: 0,
    totalDonationAmount: 0,
    totalViews: 0
  });
  const alert = useAlert();

  useEffect(() => {
    fetchStats();
  }, [timeRange, platform]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/stats/stream-stats', {
        params: { timeRange, platform }
      });
      setStats(response.data.stats);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching stats:', error);
      alert.error('Fehler beim Laden der Stats');
    } finally {
      setLoading(false);
    }
  };

  const exportStats = async () => {
    try {
      const response = await axios.get('/api/stats/export', {
        params: { timeRange, platform },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stats-${timeRange}-${platform}-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert.success('Stats exportiert!');
    } catch (error) {
      console.error('Error exporting stats:', error);
      alert.error('Fehler beim Exportieren');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <div className="stats-new">
      <div className="page-header">
        <h2>📊 Stream Statistics</h2>
        <p>Detaillierte Analyse deiner Stream-Performance</p>
      </div>

      <div className="stats-controls">
        <div className="filter-group">
          <label>Zeitraum:</label>
          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="24h">Letzte 24 Stunden</option>
            <option value="7d">Letzte 7 Tage</option>
            <option value="30d">Letzte 30 Tage</option>
            <option value="90d">Letzte 90 Tage</option>
            <option value="all">Alle Zeit</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Plattform:</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="all">Alle Plattformen</option>
            <option value="twitch-main">Twitch Main</option>
            <option value="twitch-247">Twitch 24/7</option>
            <option value="youtube">YouTube</option>
            <option value="discord">Discord</option>
          </select>
        </div>

        <button className="btn-primary" onClick={exportStats}>
          📥 Export CSV
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Lade Stats...</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="summary-grid">
            <div className="summary-card followers">
              <div className="card-icon">👥</div>
              <div className="card-content">
                <h3>Followers</h3>
                <p className="card-value">{summary.totalFollowers.toLocaleString('de-DE')}</p>
              </div>
            </div>

            <div className="summary-card subscribers">
              <div className="card-icon">⭐</div>
              <div className="card-content">
                <h3>Subscribers</h3>
                <p className="card-value">{summary.totalSubs.toLocaleString('de-DE')}</p>
              </div>
            </div>

            <div className="summary-card donations">
              <div className="card-icon">💰</div>
              <div className="card-content">
                <h3>Donations</h3>
                <p className="card-value">{summary.totalDonations}</p>
                <p className="card-subvalue">{formatCurrency(summary.totalDonationAmount)}</p>
              </div>
            </div>

            <div className="summary-card views">
              <div className="card-icon">👁️</div>
              <div className="card-content">
                <h3>Views</h3>
                <p className="card-value">{summary.totalViews.toLocaleString('de-DE')}</p>
              </div>
            </div>
          </div>

          {/* Detailed Stats Tables */}
          <div className="stats-tables">
            {/* Followers */}
            {stats.followers.length > 0 && (
              <div className="stats-table-container">
                <h3>👥 Neue Followers</h3>
                <div className="table-wrapper">
                  <table className="stats-table">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Plattform</th>
                        <th>Anzahl</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.followers.map((item, index) => (
                        <tr key={index}>
                          <td>{formatDate(item.timestamp)}</td>
                          <td>
                            <span className={`platform-badge ${item.platform}`}>
                              {item.platform}
                            </span>
                          </td>
                          <td className="stat-value">+{item.stat_value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Subscribers */}
            {stats.subscribers.length > 0 && (
              <div className="stats-table-container">
                <h3>⭐ Neue Subscribers</h3>
                <div className="table-wrapper">
                  <table className="stats-table">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Plattform</th>
                        <th>Anzahl</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.subscribers.map((item, index) => (
                        <tr key={index}>
                          <td>{formatDate(item.timestamp)}</td>
                          <td>
                            <span className={`platform-badge ${item.platform}`}>
                              {item.platform}
                            </span>
                          </td>
                          <td className="stat-value">+{item.stat_value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Views */}
            {stats.views.length > 0 && (
              <div className="stats-table-container">
                <h3>👁️ Views</h3>
                <div className="table-wrapper">
                  <table className="stats-table">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Plattform</th>
                        <th>Anzahl</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.views.map((item, index) => (
                        <tr key={index}>
                          <td>{formatDate(item.timestamp)}</td>
                          <td>
                            <span className={`platform-badge ${item.platform}`}>
                              {item.platform}
                            </span>
                          </td>
                          <td className="stat-value">{item.stat_value.toLocaleString('de-DE')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default StatsNew;
